import { toast } from '@/components/ui'
import { useUpdateOrderNotes } from '../api/queries'
import type { OpenOrderRow } from '../api/types'

/**
 * Directly-editable "Notes" column (`notes_internal`). Uncontrolled input,
 * commits on blur/Enter — no per-keystroke save, matches the pattern in
 * `sales-inbox/components/SalesIntakeLineGrid.tsx`.
 *
 * `onClick`/`onMouseDown` stop propagation because `DataTable`'s row
 * `onClick` (used here to open `OrderDrawer`) has no interactive-element
 * guard of its own — every other editable/clickable cell in this codebase
 * hand-rolls the same stopPropagation convention.
 */
export function NotesCell({ row }: { row: OpenOrderRow }) {
  const mutation = useUpdateOrderNotes()

  function commit(value: string) {
    const current = row.notes_internal ?? ''
    if (value === current) return
    mutation.mutate(
      { sol_id: row.sol_id, notes_internal: value },
      { onError: () => toast.danger('Failed to save note') },
    )
  }

  return (
    <input
      key={row.notes_internal ?? ''}
      defaultValue={row.notes_internal ?? ''}
      placeholder="Add note…"
      aria-label={`Notes for line ${row.line}`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur()
      }}
      className="w-full rounded-xs border-none bg-transparent px-1 py-0.5 text-role-table-cell outline-none focus:bg-surface-hover"
    />
  )
}
